import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-community";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {Box, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useProductCodeRepository} from "../../../api/repositories/ProductCodeRepository";
import {dateFormatterShort, fromGenericBeToGenericFe, onGridReady, useActionColDef, resolveFilterCodes} from "../../../utils";
import DialogComponent from "../../../common/components/DialogComponent";
import {
    DialogInterface,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE
} from "../../../api/types/generic";
import {AgGridReact} from "ag-grid-react";
import BasicToolbar from "../../../common/components/BasicToolbar";
import {useNavigate} from "react-router-dom";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import Loading from "../../../common/components/Loading";
import {useProductCodeLocationGroupRepository} from "../../../api/repositories/ProductCodeLocationGroupRepository";
import {useExceptionListRepository} from "../../../api/repositories/ExceptionListRepository";
import {DEFAULT_PLAN_COL_DEF} from "../../../constants";
import ActionsPopover from "../../../common/components/ActionsPopover";
import {
    useExceptionListProductCodeLocationGroupRepository
} from "../../../api/repositories/ExceptionListProductCodeLocationGroupRepository";
import SearchFilters from "../../../common/components/SearchFilters";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {FormSearchAssociationPlanModel} from "../../../api/types/associationPlan";
import {useRecoilState} from "recoil";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

type IProductData = {
    id: string;
    productCodeName: string;
}
type IProductLocationGroupData = IProductData & {
    locationGroupId: number | null;
    locationGroupName: string | null;
}
type IExceptionListPlanData = {
    id: string;
    productCodeName: string;
    locationGroupId: number | null;
    locationGroupName: string | null;
    exceptionList: string | null;
    exceptionListId: number | null;
    validityDateFrom: string | null;
    validityDateTo: string | null;
}

const ExceptionProductCodeLocationGroupList = () => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Navigate
    const navigate = useNavigate();

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    // Use Repository Hooks
    const {getPagedProductCode} = useProductCodeRepository();
    const {getLocationGroupsByProductCodePaged} = useProductCodeLocationGroupRepository();
    const {getExceptionPagedProductPlan} = useExceptionListRepository();
    const {postExceptionListProductCodeLocationGroupAll} = useExceptionListProductCodeLocationGroupRepository();
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId
    } = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchAssociationPlanModel>({
        commercialCategoryId: null,
        commercialSegmentId: null,
        productCode: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    const handleTriggerFilters = (entries: FormSearchAssociationPlanModel) => {
        const hardcodedFilter = Object.entries(entries).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [key]: {type: "contains", filter: value}
            }),
            {}
        );

        gridRef.current?.api.paginationGoToFirstPage();
        gridRef.current?.api.setFilterModel(hardcodedFilter);
        gridRef.current?.api.onFilterChanged();
    };

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        // LPP-24637: dealer e legalEntity sono obbligatori per il servizio di pricing.
        // Il reset deve ripristinare i valori del profilo utente (come lo stato iniziale),
        // non svuotarli: con le liste vuote la ricerca viene rifiutata con 400 "Validation failure".
        setTestFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            productCode: null,
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
        handleTriggerFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            productCode: null,
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            commercialCategoryId: (testFilters.commercialCategoryId as SelectElementModelFE)?.code as string,
            commercialSegmentId: (testFilters.commercialSegmentId as SelectElementModelFE)?.code as string,
            productCode: testFilters.productCode,
            legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
            dealers: resolveFilterCodes(testFilters.dealers, dealerFilteredOptions),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    // Ref per tracciare il segment corrente e ignorare risposte obsolete
    const currentSegmentRef = useRef<string | null>(null);
    const currentLegalEntitiesRef = useRef<string>("");

    const handleGetStaticContent = async () => {
        const commercialCategoriesData: CommercialCategoryEntityResponse[] = await getCommercialCategories();

        if (!currentSegmentRef.current && !currentLegalEntitiesRef.current) {
            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryList(commercialCategoryList);
        }

        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] = fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    useEffect(() => {
        const segmentCode = testFilters.commercialSegmentId
            ? (testFilters.commercialSegmentId as SelectElementModelFE).code as string
            : null;

        const legalEntitiesKey = testFilters.legalEntities && Array.isArray(testFilters.legalEntities)
            ? resolveFilterCodes(testFilters.legalEntities, []).sort().join(",")
            : "";

        currentSegmentRef.current = segmentCode;
        currentLegalEntitiesRef.current = legalEntitiesKey;

        const getContent = async () => {
            let commercialCategoriesData: CommercialCategoryEntityResponse[];

            if (segmentCode) {
                commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId(segmentCode);
            } else {
                commercialCategoriesData = await getCommercialCategories();
            }

            if (currentSegmentRef.current !== segmentCode || currentLegalEntitiesRef.current !== legalEntitiesKey) {
                return;
            }

            const allCategories: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);

            const selectedLegalEntities = testFilters.legalEntities;
            const selectedLegalEntityCodes = resolveFilterCodes(selectedLegalEntities, []);
            // null = nessun filtro per legal entity (mostra tutte le categorie)
            let legalEntityCodes: string[] | null = selectedLegalEntityCodes.length > 0
                ? selectedLegalEntityCodes
                : null;

            const expandedCategories = new Map<string, CommercialCategorySelectFE>();
            allCategories.forEach(category => {
                if (category.labels && category.labels.length > 0) {
                    category.labels.forEach(labelObj => {
                        if (!legalEntityCodes || legalEntityCodes.includes(labelObj.country)) {
                            const key = labelObj.label;
                            if (!expandedCategories.has(key)) {
                                expandedCategories.set(key, {
                                    ...category,
                                    label: labelObj.label
                                });
                            }
                        }
                    });
                } else {
                    expandedCategories.set(category.label, category);
                }
            });
            setCommercialCategoryList(Array.from(expandedCategories.values()));
        };
        getContent();
    }, [testFilters.commercialSegmentId, testFilters.legalEntities]);

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "productCode",
            value: testFilters.productCode,
            label: `${t("pricing:associationPlan.form.labels.productCode")}`,
            handleChange: (value: string) => {
                setTestFilters({
                    ...testFilters,
                    productCode: value
                });
            },
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegmentId",
            value: testFilters.commercialSegmentId,
            label: `${t("pricing:associationPlan.form.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null
                });
            },
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId,
            label: `${t("pricing:associationPlan.form.labels.commercialCategory")}`,
            handleChange: (value: CommercialCategorySelectFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                });
            },
            options: commercialCategoryList,
            type: "Autocomplete"
        }
    ];

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOption || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    // ************ AG GRID START ************ //
    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "id",
            headerName: t("pricing:exceptionListProductCodeLocationGroup.table.headers.productCode"),
            rowGroup: true,
            hide: true,
            pinned: "left",
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => params.data.id + " - " + params.data.productCodeName
        },
        {
            field: "legalEntities",
            headerName: t("pricing:peaks.table.headers.legalEntities"),
            headerTooltip: t("pricing:upsellingRate.table.headers.legalEntities"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            pinned: "left",
            hide: true
        },
        {
            field: "dealers",
            headerName: t("pricing:peaks.table.headers.dealers"),
            headerTooltip: t("pricing:upsellingRate.table.headers.dealers"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.dealer)),
            pinned: "left",
            hide: true
        },
        {
            field: "locationGroupId",
            headerName: t("pricing:exceptionListProductCodeLocationGroup.table.headers.locationGroup"),
            rowGroup: true,
            hide: true,
            pinned: "left",
            cellRenderer: (params: ICellRendererParams) => params.data.locationGroupName
        },
        {
            field: "exceptionList",
            flex: 1,
            headerName: t("pricing:exceptionListProductCodeLocationGroup.table.headers.exceptionList"),
            headerTooltip: t("pricing:exceptionListProductCodeLocationGroup.table.headers.exceptionList")
        },
        {
            field: "validityDateFrom",
            flex: 1,
            headerName: t("pricing:exceptionListProductCodeLocationGroup.table.headers.validityDateFrom"),
            headerTooltip: t("pricing:exceptionListProductCodeLocationGroup.table.headers.validityDateFrom")
        },
        {
            field: "validityDateTo",
            flex: 1,
            headerName: t("pricing:exceptionListProductCodeLocationGroup.table.headers.validityDateTo"),
            headerTooltip: t("pricing:exceptionListProductCodeLocationGroup.table.headers.validityDateTo")
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => {
                return params.data.exceptionList &&
                    <ActionsPopover
                        params={params}
                        update={{
                            action: () => navigate("/pricing/product-code/location-group-exception-list/create", {
                                replace: true,
                                state: {
                                    exceptionListId: params.data.exceptionListId
                                }
                            }), userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_UPDATE_EXCEPTION_LIST_PLAN_LIST"
                                })
                            })
                        }}
                        del={{
                            action: async () => {
                                await postExceptionListProductCodeLocationGroupAll({
                                    associationsToSave: [],
                                    associationsToDelete: [{
                                        exceptionListId: params.data.exceptionListId,
                                        productCodeId: params.data.id,
                                        locationGroupId: params.data.locationGroupId
                                    }]
                                });
                                gridRef.current!.api.refreshServerSide({purge: true});
                            }, userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_DELETE_EXCEPTION_LIST_PLAN_LIST"
                                })
                            })
                        }}
                    />;
            }
        },


        {
            field: "productCode",
            colId: "productCode",
            hide: true,
            filter: "text"
        },
        {
            field: "commercialCategoryId",
            colId: "commercialCategoryId",
            hide: true,
            filter: "text"
        },
        {
            field: "commercialSegmentId",
            colId: "commercialSegmentId",
            hide: true,
            filter: "text"
        }
    ];
    const autoGroupColumnDef = useMemo<ColDef>(() => {
        return {
            minWidth: 200
        };
    }, []);

    const defaultColDef = DEFAULT_PLAN_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchAssociationPlanModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce((acc, [key, value]) => ({
                    ...acc,
                    [key]: value?.filter || ""
                }), testFilters);

                let rowCount = 0;
                let rowData: IProductData[] | IProductLocationGroupData[] | IExceptionListPlanData[] = [];
                // @ts-ignore
                let size = params.request.endRow - params.request.startRow;
                // @ts-ignore
                let page = params.request.startRow > 0 ? Math.floor(params.request.startRow / size) : 0;

                const groupKeys = params.request.groupKeys;
                if (!groupKeys || groupKeys.length === 0) {
                    //caricamento codici prodotto
                    const productCodePaged = await getPagedProductCode({
                        ...filter,
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    if (productCodePaged.totalElements === 0) {
                        gridRef.current!.api.showNoRowsOverlay();
                    } else {
                        gridRef.current!.api.hideOverlay();
                    }
                    rowCount = productCodePaged.totalElements;
                    rowData = productCodePaged.content.map(pc => ({
                        id: pc.id as string,
                        productCodeName: pc.name
                    })) as IProductData[];
                }

                //caricamento gruppi di location
                else if (groupKeys.length === 1 && groupKeys[0]) {
                    const locationGroupList = await getLocationGroupsByProductCodePaged({
                        productCode: groupKeys[0],
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });
                    rowCount = locationGroupList.totalElements;
                    rowData = locationGroupList.content.map(lg => ({
                        id: groupKeys[0] as string,
                        productCodeName: groupKeys[0] as string,
                        locationGroupId: lg.id,
                        locationGroupName: lg.name
                    })) as IProductLocationGroupData[];
                }
                //caricamento exceptionPlan
                else if (groupKeys.length === 2 && groupKeys[0] && groupKeys[1]) {
                    const exceptionLists = await getExceptionPagedProductPlan({
                        productCode: groupKeys[0],
                        locationGroup: Number(groupKeys[1]),
                        dealers: testFilters.dealers,
                        legalEntities: testFilters.legalEntities,
                        showOnlyActive: true,
                        sort: sort && order ? `${sort},${order}` : "",
                        size: size,
                        page: page
                    });

                    rowCount = exceptionLists.totalElements;
                    rowData = exceptionLists.content.map(exceptionList => ({
                        id: groupKeys[0] as string,
                        productCodeName: "",
                        locationGroupId: Number(groupKeys[1]),
                        locationGroupName: "",
                        exceptionList: exceptionList.name,
                        exceptionListId: exceptionList.id,
                        validityDateFrom: dateFormatterShort(exceptionList?.from),
                        validityDateTo: dateFormatterShort(exceptionList?.to)
                    })) as IExceptionListPlanData[]
                }

                params.success({
                    rowData: rowData,
                    rowCount: rowCount
                });
            }
        };
    };
    // ************ AG GRID END ************ //

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_EXCEPTION_LIST_PLAN_LIST";
                        });
                    })
                        ? {
                            onClick: () =>
                                navigate(
                                    "/pricing/product-code/location-group-exception-list/create"
                                ),
                        }
                        : undefined
                }
            />
        ),
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCEPTION_LIST_PLAN_LIST.name")}/>

            <SearchFilters
                filtersOutOfCollapse={filtersOutOfCollapse}
                filtersInCollapse={filtersInCollapse}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        autoGroupColumnDef={autoGroupColumnDef}
                        animateRows={true}
                        toolbar={toolbar}
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        serverSideFilteringAlwaysResets={true}
                        onGridReady={() => onGridReady({
                            initialSortColId: "name",
                            gridRef,
                            createDatasource
                        })}
                        groupDisplayType={"multipleColumns"}
                    />
                    {dialog?.open &&
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    }
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default ExceptionProductCodeLocationGroupList;